<template>
    <b-form>
        <b-link
            :to="{ name: 'projects.components.floors.rooms.walls.show', params: { projectId, componentId, floorId, roomId, wallId: wall.id } }"
        >
            {{ wall.name }}
        </b-link>
        <template v-if="item.foundation && item.foundation.foundation">
            ( {{ item.foundation.foundation.short_name }} )
        </template>
        ( {{ $t('Width: {0} m', [wall.width]) }})

        <b-form-group
            v-if="$gate.allow('update', wall)"
            :label="$t('Width')"
            :label-for="`wall_${wall.id}_width`"
            :state="item.errors.has('width') ? false : null"
            label-sr-only
        >
            <b-input-group size="sm">
                <b-form-input
                    :id="`wall_${wall.id}_width`"
                    v-model="item.width"
                    :disabled="wall.type !== 'wall'"
                    :state="item.errors.has('width') ? false : null"
                    name="width"
                    size="sm"
                    step="0.001"
                    style="width: 80px;"
                    type="number"
                    @change="handleUpdate"
                />

                <b-input-group-append>
                    <b-input-group-text>m</b-input-group-text>

                    <b-button
                        v-if="wall.type === 'wall'"
                        :id="`wall_${wall.id}_popuper`"
                        ref="button"
                        :title="$t('Actions')"
                    >
                        <fa icon="ellipsis-v" />
                    </b-button>
                </b-input-group-append>
            </b-input-group>
        </b-form-group>

        <b-popover
            v-if="$gate.allow('update', wall)"
            :container="`room-overview_${roomId}`"
            :show.sync="popoverShow"
            :target="`wall_${wall.id}_popuper`"
            boundary="window"
            placement="righttop"
            triggers="click"
            @hidden="onHidden"
        >
            <template slot="title">
                <b-button :aria-label="$t('Close')" class="close" @click="onClose">
                    <span aria-hidden="true" class="d-inline-block">&times;</span>
                </b-button>
                {{ wall.name }}
            </template>

            <b-form-group
                :invalid-feedback="item.errors.get('name')"
                :label="$t('Name')"
                :label-for="'wall_' + wall.id + 'name'"
                :state="item.errors.has('name') ? false : null"
                class="mb-1"
                label-align="right"
                label-cols="4"
            >
                <b-form-input
                    :id="'wall_' + wall.id + 'name'"
                    v-model="item.name"
                    :state="item.errors.has('name') ? false : null"
                    class="form-control-sm"
                    name="name"
                    size="sm"
                    @change="handleUpdate"
                />
            </b-form-group>

            <b-form-group
                :invalid-feedback="item.errors.get('height')"
                :label="$t('Height')"
                :label-for="`wall_${wall.id}_height`"
                :state="item.errors.has('height') ? false : null"
                class="mb-1"
                label-align="right"
                label-cols="4"
            >
                <b-input-group append="m" size="sm">
                    <b-form-input
                        :id="`wall_${wall.id}_height`"
                        v-model="item.height"
                        :state="item.errors.has('height') ? false : null"
                        min="0"
                        name="height"
                        size="sm"
                        step="0.001"
                        type="number"
                        @change="handleUpdate"
                    />
                </b-input-group>
            </b-form-group>

            <b-form-group
                :invalid-feedback="item.errors.get('foundation_id')"
                :label="$t('Foundation')"
                :label-for="`wall_${wall.id}_foundation`"
                :state="item.errors.has('foundation_id') ? false : null"
                label-align="right"
                label-cols="4"
            >
                <foundation-select
                    :id="`wall_${wall.id}_foundation`"
                    ref="foundationInput"
                    v-model="foundation"
                    :resource="`projects/${projectId}/foundations`"
                    :state="item.errors.has('foundation_id') ? false : null"
                    name="foundation_id"
                    size="sm"
                    @input="handleFoundationInput"
                />
            </b-form-group>

            <services-select
                v-model="item.services"
                :element-type="item.type"
                :project-id="projectId"
            />

            <div class="text-right">
                <b-button size="sm" variant="danger" @click="handleDelete">
                    {{ $t('Delete') }}
                    <fa class="ml-1" icon="trash" />
                </b-button>
            </div>
        </b-popover>
    </b-form>
</template>
<script>
    import Form from 'vform';
    import FoundationSelect from '@/components/selects/ResourceSelect';
    import ServicesSelect from '@/components/ServicesSelect';
    import deleteItem from "@/mixins/deleteItem";

    export default {
        name: 'RoomWall',
        components: {ServicesSelect, FoundationSelect},
        mixins:[deleteItem],
        props: {
            projectId: {
                type: [Number, String],
                required: true,
            },
            componentId: {
                type: [Number, String],
                required: true,
            },
            floorId: {
                type: [Number, String],
                required: true,
            },
            roomId: {
                type: [Number, String],
                required: true,
            },
            wall: {
                type: Object,
                required: true,
            },
        },
        data() {
            return {
                item: new Form({
                    name: null,
                    width: null,
                    height: null,
                    foundation_id: null,
                    services: []
                }),
                foundation_id: null,
                popoverShow: false,
                services: []
            };
        },
        computed: {
            resourceName() {
                return 'walls'
            },
            apiUrl() {
                return `/api/projects/${this.projectId}/components/${this.componentId}/floors/${this.floorId}/rooms/${this.roomId}/walls/${this.wall.id}`;
            },
            deleteApiUrl() {
                return `/api/projects/${this.projectId}/components/${this.componentId}/floors/${this.floorId}/rooms/${this.roomId}/walls/${this.wall.id}`;
            },
        },
        watch: {
            wall: {
                handler(newWall) {
                    this.item.fill(newWall);
                    this.foundation = newWall.foundation
                },
                immediate: true
            },
        },
        methods: {
            handleFoundationInput(event) {
                this.item.foundation_id = event ? event.id : null;
                this.handleUpdate();
            },
            handleUpdate() {
                this.item.put(this.apiUrl).then(() => {
                    this.item = new Form(this.item.data());
                }).catch(e => e.response || throw e);

                this.$emit('updated', this.wall);
            },

            onClose() {
                this.popoverShow = false;
            },

            onHidden() {
                // Called just after the popover has finished hiding
                // Bring focus back to the button
                this.focusRef(this.$refs.button);
            },
        },
    };
</script>
